import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, MapPin, Clock } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';
import { IMG } from '../lib/images';
import { SEO } from '../components/SEO';
import { HOME } from '../constants/testIds';

export default function HomePage() {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const categories = t('collections.categories');
  const imgMap = {
    femme: IMG.catFemme,
    homme: IMG.catHomme,
    chaussant: IMG.sandals,
    accessoires: IMG.catAccessories,
  };

  return (
    <main>
      <SEO
        title={t('common.tagline')}
        description="Tahitian Move — marque polynésienne de mode depuis 1977. Prêt-à-porter, savates, accessoires. Boutique à Papeete, Tahiti."
      />

      {/* HERO */}
      <section
        data-testid={HOME.hero}
        className="relative h-screen min-h-[700px] w-full overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={IMG.heroLifestyle}
            alt="Tahitian Move — Polynesian lifestyle"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="relative h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-24 md:pb-32">
          <p className="eyebrow text-white/80 reveal">{t('home.heroEyebrow')}</p>
          <h1 className="display text-white text-[15vw] md:text-[9rem] lg:text-[11rem] mt-6 reveal reveal-delay-1">
            {t('home.heroTitle')}
          </h1>
          <p className="text-white/85 font-light text-base md:text-lg max-w-xl mt-8 leading-relaxed reveal reveal-delay-2">
            {t('home.heroSub')}
          </p>
          <div className="flex flex-wrap gap-4 mt-10 reveal reveal-delay-3">
            <Link to="/heritage" data-testid={HOME.heroCta} className="btn-ghost-light">
              {t('common.discoverHeritage')} <ArrowUpRight size={14} />
            </Link>
            <Link to="/collections" data-testid={HOME.heroSecondary} className="btn-ghost-light">
              {t('common.discoverCollections')}
            </Link>
          </div>
        </div>

        {/* Bottom badge */}
        <div className="absolute right-6 md:right-10 top-1/2 -translate-y-1/2 hidden md:block">
          <div className="rotate-90 origin-right text-white/60 text-xs tracking-[0.4em] uppercase">
            Papeete · French Polynesia
          </div>
        </div>
      </section>

      {/* HERITAGE BLOCK */}
      <section
        data-testid={HOME.heritageBlock}
        className="bg-background py-24 md:py-40"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-6 lg:col-span-5">
            <p className="eyebrow">{t('home.heritageEyebrow')}</p>
            <h2 className="display text-5xl md:text-6xl lg:text-7xl mt-6 text-ink">
              {t('home.heritageTitle')}
            </h2>
            <p className="text-ink-soft mt-8 text-base md:text-lg leading-relaxed max-w-lg">
              {t('home.heritageBody')}
            </p>
            <Link to="/heritage" className="btn-outline mt-10">
              {t('common.learnMore')} <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="md:col-span-6 lg:col-span-7 grid grid-cols-2 gap-3 md:gap-5">
            <div className="media-card aspect-[3/4] mt-12">
              <img src={IMG.heritageVintage} alt="Heritage 1977" className="w-full h-full object-cover" />
            </div>
            <div className="media-card aspect-[3/4]">
              <img src={IMG.heritageCouple} alt="Tahitian heritage" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE — heritage statement */}
      <section className="bg-ocean text-white py-10 overflow-hidden border-y border-white/5">
        <div className="marquee-track flex gap-16 items-center whitespace-nowrap">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-16 items-center">
              <span className="font-serif italic text-2xl md:text-3xl">Since 1977</span>
              <span className="text-lagoon">✦</span>
              <span className="eyebrow text-white/70">Polynésie française</span>
              <span className="text-lagoon">✦</span>
              <span className="font-serif italic text-2xl md:text-3xl">Exprime ton Style</span>
              <span className="text-lagoon">✦</span>
              <span className="eyebrow text-white/70">Made in Tahiti</span>
              <span className="text-lagoon">✦</span>
            </div>
          ))}
        </div>
      </section>

      {/* COLLECTIONS BENTO */}
      <section data-testid={HOME.collectionsBlock} className="bg-paper py-24 md:py-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 items-end">
            <div className="md:col-span-7">
              <p className="eyebrow">{t('home.collectionsEyebrow')}</p>
              <h2 className="display text-5xl md:text-6xl lg:text-7xl mt-6 text-ink">
                {t('home.collectionsTitle')}
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-ink-soft text-base md:text-lg leading-relaxed">
                {t('home.collectionsBody')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-3 md:gap-5">
            {categories.map((cat, idx) => {
              // bento layout: 0 -> col-span-7 row-span-2, 1 -> 5, 2 -> 5, 3 -> 7
              const layoutClass =
                idx === 0
                  ? 'col-span-12 md:col-span-7 md:row-span-2 aspect-[4/3] md:aspect-auto md:h-[640px]'
                  : idx === 3
                  ? 'col-span-12 md:col-span-7 aspect-[4/3] md:aspect-[5/3]'
                  : 'col-span-6 md:col-span-5 aspect-[4/5] md:aspect-[5/4]';
              return (
                <Link
                  to="/collections"
                  key={cat.key}
                  data-testid={`home-cat-${cat.key}`}
                  className={`media-card relative group ${layoutClass}`}
                >
                  <img
                    src={imgMap[cat.key]}
                    alt={cat.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                  <div className="absolute left-0 right-0 bottom-0 p-6 md:p-8 flex items-end justify-between gap-4">
                    <div>
                      <p className="eyebrow text-white/70">0{idx + 1}</p>
                      <h3 className="font-serif text-3xl md:text-4xl text-white mt-2">
                        {cat.title}
                      </h3>
                      <p className="text-white/80 text-sm font-light max-w-sm mt-2">
                        {cat.body}
                      </p>
                    </div>
                    <ArrowUpRight className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={28} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* BOUTIQUE FOCUS */}
      <section data-testid={HOME.boutiqueBlock} className="bg-background py-24 md:py-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-6 order-2 md:order-1 media-card aspect-[4/5]">
            <img src={IMG.boutique} alt="Papeete boutique" className="w-full h-full object-cover" />
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <p className="eyebrow">{t('home.boutiqueEyebrow')}</p>
            <h2 className="display text-5xl md:text-6xl lg:text-7xl mt-6 text-ink">
              {t('home.boutiqueTitle')}
            </h2>
            <p className="text-ink-soft mt-8 text-base md:text-lg leading-relaxed max-w-lg">
              {t('home.boutiqueBody')}
            </p>
            <div className="mt-10 grid grid-cols-2 gap-8 max-w-md">
              <div>
                <div className="flex items-center gap-2 text-lagoon">
                  <MapPin size={16} />
                  <span className="eyebrow text-ink">{t('boutique.addressLabel')}</span>
                </div>
                <p className="text-ink-soft text-sm mt-3 font-light">Papeete<br/>Polynésie française</p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-lagoon">
                  <Clock size={16} />
                  <span className="eyebrow text-ink">{t('boutique.hoursLabel')}</span>
                </div>
                <p className="text-ink-soft text-sm mt-3 font-light">Lun–Ven<br/>08h30 – 17h30</p>
              </div>
            </div>
            <Link to="/boutique" className="btn-primary mt-10">
              {t('common.visitBoutique')} <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* B2B BLOCK */}
      <section data-testid={HOME.b2bBlock} className="bg-ocean text-white py-24 md:py-40 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={IMG.lagoon} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <p className="eyebrow text-lagoon">{t('home.b2bEyebrow')}</p>
            <h2 className="display text-5xl md:text-6xl lg:text-7xl mt-6">
              {t('home.b2bTitle')}
            </h2>
            <p className="text-white/75 mt-8 text-base md:text-lg leading-relaxed max-w-xl">
              {t('home.b2bBody')}
            </p>
          </div>
          <div className="md:col-span-5 md:text-right">
            <Link to="/b2b" className="btn-ghost-light">
              {t('common.becomePartner')} <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="bg-paper py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="eyebrow">{t('home.newsletterTitle')}</p>
          <h3 className="display text-4xl md:text-5xl mt-6 text-ink">
            {t('home.newsletterBody')}
          </h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (email) setSubscribed(true);
            }}
            className="mt-10 flex flex-col sm:flex-row gap-4 max-w-xl mx-auto"
          >
            <input
              data-testid={HOME.newsletterInput}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('home.newsletterPlaceholder')}
              className="tm-input flex-1 text-center sm:text-left"
              disabled={subscribed}
            />
            <button
              data-testid={HOME.newsletterSubmit}
              type="submit"
              className="btn-primary justify-center"
              disabled={subscribed}
            >
              {subscribed ? '✓' : t('home.newsletterCta')}
            </button>
          </form>
          {subscribed && (
            <p className="mt-4 text-sm text-ink-soft">Merci, à bientôt.</p>
          )}
        </div>
      </section>
    </main>
  );
}
